import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Form, Badge } from 'react-bootstrap';
import { db } from 'config/firebase';
import { collection, getDocs, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import AdvanceTableWrapper from 'components/merlin/advance-table/AdvanceTableWrapper';
import AdvanceTable from 'components/merlin/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faDownload, faUndo } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { rollbackImport } from '../helpers/promotionHelpers';
import { queryAllPromotions, computeStatus } from '../helpers/promotionCollectionHelpers';

export default function PromotionsGrid({ onRefresh }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active'); // active, expired, all
  const [sourceFilter, setSourceFilter] = useState('all'); // all, bulk_upload, manual
  const [skuSearch, setSkuSearch] = useState('');

  useEffect(() => {
    loadPromotions();
  }, []);

  async function loadPromotions() {
    try {
      setLoading(true);
      console.log('[PromotionsGrid] Loading promotions...');

      // Try promotions collection first
      let allPromotions = [];
      let useProductsFallback = false;
      try {
        allPromotions = await queryAllPromotions();
        console.log('[PromotionsGrid] Loaded from promotions collection:', allPromotions.length);
      } catch (queryError) {
        console.error('[PromotionsGrid] Error querying promotions collection:', queryError);
        console.error('[PromotionsGrid] Will use products.discounts fallback');
        useProductsFallback = true;
      }

      // Fallback: If promotions collection is empty OR query failed, load from products.discounts
      if (allPromotions.length === 0 || useProductsFallback) {
        const productsRef = collection(db, 'products');
        const productsSnapshot = await getDocs(productsRef);

        productsSnapshot.forEach(productDoc => {
          try {
            const product = productDoc.data();
            const discounts = product.discounts || [];

            discounts.forEach((discount, index) => {
              try {
                const startDate = discount.startDate instanceof Timestamp
                  ? discount.startDate.toDate()
                  : discount.startDate;
                const endDate = discount.endDate instanceof Timestamp
                  ? discount.endDate.toDate()
                  : discount.endDate;

                allPromotions.push({
                  id: `${productDoc.id}_${index}`,
                  sku: product.sku,
                  productName: product.name || product.sku,
                  productRef: productDoc.ref,
                  name: discount.name || 'Promotion',
                  value: discount.value || discount.amount || 0,
                  startDate,
                  endDate,
                  status: computeStatus(startDate, endDate),
                  createdBy: discount.createdBy || '—',
                  createdVia: discount.createdVia || 'manual',
                  createdAt: discount.createdAt,
                  batchId: discount.batchId
                });
              } catch (discountError) {
                console.error(`Error processing discount ${index} for product ${productDoc.id}:`, discountError);
              }
            });
          } catch (productError) {
            console.error(`Error processing product ${productDoc.id}:`, productError);
          }
        });
      }

      // Convert Timestamps to Dates for display and strip DocumentReference fields
      console.log('[PromotionsGrid] Processing promotions...');
      const processedPromotions = allPromotions.map((promo, index) => {
        try {
          const startDate = promo.startDate instanceof Timestamp
            ? promo.startDate.toDate()
            : promo.startDate;

          const endDate = promo.endDate instanceof Timestamp
            ? promo.endDate.toDate()
            : promo.endDate;

          // Create clean object with ONLY primitive fields - avoid DocumentReferences entirely
          return {
            id: promo.id || `unknown_${index}`,
            sku: promo.sku || '',
            productSku: promo.productSku || promo.sku || '',
            productName: promo.productName || promo.sku || '',
            name: promo.name || '',
            value: promo.value || 0,
            startDate,
            endDate,
            notes: promo.notes || '',
            createdBy: promo.createdBy || '',
            createdAt: promo.createdAt || null,
            createdVia: promo.createdVia || 'manual',
            batchId: promo.batchId || null,
            isActive: promo.isActive || false,
            status: computeStatus(startDate, endDate)
          };
        } catch (error) {
          console.error(`[PromotionsGrid] Error processing promotion ${index}:`, error);
          return null;
        }
      }).filter(Boolean); // Remove any null entries from failed processing
      console.log('[PromotionsGrid] Processed promotions:', processedPromotions.length);

      setPromotions(processedPromotions);
    } catch (error) {
      console.error('Error loading promotions:', error);
      toast.error(`Failed to load promotions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const filteredPromotions = useMemo(() => {
    let filtered = promotions;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    // Filter by source
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(p => p.createdVia === sourceFilter);
    }
    
    // Filter by SKU search
    if (skuSearch.trim()) {
      const search = skuSearch.toLowerCase().trim();
      filtered = filtered.filter(p => 
        p.sku?.toLowerCase().includes(search) || 
        p.productName?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [promotions, statusFilter, sourceFilter, skuSearch]);

  const activeCount = promotions.filter(p => p.status === 'active').length;
  const scheduledCount = promotions.filter(p => p.status === 'scheduled').length;
  const expiredCount = promotions.filter(p => p.status === 'expired').length;

  async function handleBulkDelete(selectedRowIds) {
    const selectedIds = Object.keys(selectedRowIds);
    if (selectedIds.length === 0) {
      toast.warn('No promotions selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.length} selected promotions?`)) {
      return;
    }

    try {
      // Delete from promotions collection
      for (const id of selectedIds) {
        await deleteDoc(doc(db, 'promotions', id));
      }

      toast.success(`Deleted ${selectedIds.length} promotions`);
      await loadPromotions();
      
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting promotions:', error);
      toast.error('Failed to delete promotions');
    }
  }

  async function handleRollback(batchId) {
    if (!window.confirm('Rollback this entire import batch? This cannot be undone.')) {
      return;
    }

    try {
      const result = await rollbackImport(batchId);
      if (result.success) {
        toast.success(`Rolled back ${result.removedCount} promotions`);
        await loadPromotions();
        if (onRefresh) onRefresh();
      } else {
        toast.error(`Rollback failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Rollback error:', error);
      toast.error('Rollback failed');
    }
  }

  function handleExportCSV() {
    const rows = [['SKU', 'Product Name', 'Promotion Name', 'Amount', 'Start Date', 'End Date', 'Status', 'Created By', 'Created At', 'Batch ID']];
    
    filteredPromotions.forEach(promo => {
      rows.push([
        promo.sku,
        promo.productName,
        promo.name,
        promo.value,
        promo.startDate?.toLocaleDateString(),
        promo.endDate?.toLocaleDateString(),
        promo.status.charAt(0).toUpperCase() + promo.status.slice(1),
        promo.createdBy || '—',
        promo.createdAt ? (promo.createdAt instanceof Timestamp ? promo.createdAt.toDate().toLocaleString() : '—') : '—',
        promo.batchId || '—'
      ]);
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `promotions_${statusFilter}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const statusColors = {
    active: 'success',
    scheduled: 'info',
    expired: 'secondary'
  };

  const statusLabels = {
    active: 'Active',
    scheduled: 'Scheduled',
    expired: 'Expired'
  };

  const columns = useMemo(() => [
    {
      accessor: 'status',
      Header: 'Status',
      Cell: ({ row }) => (
        <SoftBadge bg={statusColors[row.original.status] || 'secondary'}>
          {statusLabels[row.original.status] || row.original.status}
        </SoftBadge>
      )
    },
    {
      accessor: 'sku',
      Header: 'SKU'
    },
    {
      accessor: 'productName',
      Header: 'Product',
      Cell: ({ row }) => (
        <span className="text-muted fs--1">{row.original.productName}</span>
      )
    },
    {
      accessor: 'name',
      Header: 'Promotion Name'
    },
    {
      accessor: 'value',
      Header: 'Amount',
      Cell: ({ row }) => (
        <span>${Number(row.original.value).toFixed(2)}</span>
      )
    },
    {
      accessor: 'startDate',
      Header: 'Start Date',
      Cell: ({ row }) => row.original.startDate?.toLocaleDateString(),
      sortType: (rowA, rowB) => {
        const a = rowA.original.startDate?.getTime() || 0;
        const b = rowB.original.startDate?.getTime() || 0;
        return a - b;
      }
    },
    {
      accessor: 'endDate',
      Header: 'End Date',
      Cell: ({ row }) => row.original.endDate?.toLocaleDateString(),
      sortType: (rowA, rowB) => {
        const a = rowA.original.endDate?.getTime() || 0;
        const b = rowB.original.endDate?.getTime() || 0;
        return a - b;
      }
    },
    {
      accessor: 'createdBy',
      Header: 'Created By',
      Cell: ({ row }) => (
        <span className="text-muted fs--2">{row.original.createdBy || '—'}</span>
      )
    },
    {
      accessor: 'createdVia',
      Header: 'Source',
      Cell: ({ row }) => {
        const source = row.original.createdVia;
        return (
          <Badge bg={source === 'bulk_upload' ? 'info' : 'secondary'} className="fs--2">
            {source === 'bulk_upload' ? 'Bulk' : 'Manual'}
          </Badge>
        );
      }
    },
    {
      accessor: 'actions',
      Header: 'Actions',
      Cell: ({ row }) => {
        const batchId = row.original.batchId;
        return batchId ? (
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleRollback(batchId)}
            title="Rollback entire import batch"
          >
            <FontAwesomeIcon icon={faUndo} />
          </Button>
        ) : null;
      }
    }
  ], []);

  function BulkActions({ selectedRowIds }) {
    const selectedCount = Object.keys(selectedRowIds).length;

    return (
      <Row className="flex-between-center mb-3">
        <Col xs={12} md={6} className="d-flex align-items-center">
          <h5 className="mb-0 me-3">
            {selectedCount > 0 ? `Selected ${selectedCount} promotions` : 'All Promotions'}
          </h5>
          <div>
            <Badge bg="success" className="me-1">{activeCount} Active</Badge>
            <Badge bg="info" className="me-1">{scheduledCount} Scheduled</Badge>
            <Badge bg="secondary">{expiredCount} Expired</Badge>
          </div>
        </Col>
        <Col xs={12} md={6} className="text-end d-flex justify-content-end gap-2">
          <Form.Control
            size="sm"
            type="text"
            placeholder="Search by SKU..."
            style={{ width: '150px' }}
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
          />
          
          <Form.Select
            size="sm"
            style={{ width: '120px' }}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            <option value="bulk_upload">Bulk Only</option>
            <option value="manual">Manual Only</option>
          </Form.Select>
          
          <Form.Select
            size="sm"
            style={{ width: '120px' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="expired">Expired</option>
            <option value="all">All Status</option>
          </Form.Select>
          
          {selectedCount > 0 && (
            <Button
              variant="danger"
              size="sm"
              className="me-2"
              onClick={() => handleBulkDelete(selectedRowIds)}
            >
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Delete Selected
            </Button>
          )}
          
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleExportCSV}
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Export CSV
          </Button>
        </Col>
      </Row>
    );
  }

  if (loading) {
    return <div className="text-center py-5">Loading promotions...</div>;
  }

  return (
    <AdvanceTableWrapper
      columns={columns}
      data={filteredPromotions}
      sortable
      pagination
      perPage={25}
      selection
      selectionColumnWidth={30}
    >
      <BulkActions table />
      <div className="table-responsive">
        <AdvanceTable
          table
          headerClassName="bg-200 text-900 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            striped: true,
            className: 'fs--1 mb-0'
          }}
        />
      </div>
    </AdvanceTableWrapper>
  );
}
